import React, { useMemo, useState } from 'react';
import { FormSchemaUI, FormField } from '../core/services/formSchema';
import type { NormalizeResult } from '../hooks/useNormalize';
import clsx from 'clsx';

export type SchemaFormProps = {
  schema: FormSchemaUI;
  value?: Record<string, any>;
  onChange?: (next: Record<string, any>) => void;
  onSubmit?: (data: Record<string, any>) => void;
  disabled?: boolean;
  normalizeMode?: 'blur' | 'submit' | 'both' | 'none';
  onNormalize?: (values: Record<string, any>) => Promise<NormalizeResult>;
};

function mask(value: any, redact?: boolean): string {
  if (!redact) return String(value ?? '');
  const s = String(value ?? '');
  return s.length ? '***' : '';
}

function FieldView({ field, val, onFieldChange, disabled, onBlur }: { field: FormField; val: any; onFieldChange: (name: string, v: any) => void; disabled?: boolean; onBlur?: () => void }) {
  const common = {
    id: `f_${field.name}`,
    name: field.name,
    placeholder: field.placeholder,
    disabled,
    onBlur
  } as any;

  if (Array.isArray(field.enum) && field.enum.length > 0) {
    return (
      <div className="form-row">
        <label htmlFor={common.id}>{field.label ?? field.name}{field.required ? ' *' : ''}</label>
        <select {...common} value={String(val ?? '')} onChange={e => onFieldChange(field.name, e.target.value)}>
          <option value="" disabled>{field.placeholder ?? 'Select...'}</option>
          {field.enum.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {field.description && <small>{field.description}</small>}
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div className="form-row">
        <label htmlFor={common.id}>
          <input type="checkbox" {...common} checked={!!val} onChange={e => onFieldChange(field.name, e.target.checked)} /> {field.label ?? field.name}
        </label>
        {field.description && <small>{field.description}</small>}
      </div>
    );
  }

  // default to text input
  return (
    <div className="form-row">
      <label htmlFor={common.id}>{field.label ?? field.name}{field.required ? ' *' : ''}</label>
      <input
        type="text"
        {...common}
        value={field.redact ? mask(val, true) : String(val ?? '')}
        onChange={e => onFieldChange(field.name, e.target.value)}
        style={{ width: '100%', color: '#111', backgroundColor: 'var(--main-text)' }}
      />
      {field.description && <small>{field.description}</small>}
    </div>
  );
}

export const SchemaForm: React.FC<SchemaFormProps> = ({ schema, value, onChange, onSubmit, disabled, normalizeMode = 'none', onNormalize }) => {
  const [local, setLocal] = useState<Record<string, any>>(value ?? {});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fields = useMemo(() => schema.fields ?? [], [schema]);

  const handleFieldChange = (name: string, v: any) => {
    const next = { ...local, [name]: v };
    setLocal(next);
    onChange?.(next);
  };

  const handleBlurNormalize = async () => {
    if (!onNormalize) return;
    if (!(normalizeMode === 'blur' || normalizeMode === 'both')) return;
    const result = await onNormalize(local);
    const next = result.resolved ?? local;
    setLocal(next);
    onChange?.(next);
    const invalid = result.invalid || {};
    const missing = result.missing || {};
    const merged: Record<string, string> = {};
    Object.assign(merged, invalid, missing);
    setErrors(merged);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    const maybeNormalize = async () => {
      if (onNormalize && (normalizeMode === 'submit' || normalizeMode === 'both')) {
        const res = await onNormalize(local);
        const next = res.resolved ?? local;
        setLocal(next);
        onChange?.(next);
        const invalid = res.invalid || {};
        const missing = res.missing || {};
        const merged: Record<string, string> = {};
        Object.assign(merged, invalid, missing);
        setErrors(merged);
        return next;
      }
      return local;
    };
    void (async () => {
      const finalVals = await maybeNormalize();
      onSubmit?.(finalVals);
    })();
  };

  return (
    <form onSubmit={handleSubmit} className={clsx('schema-form')}>
      {fields.map(f => (
        <FieldView key={f.name} field={f} val={local[f.name]} onFieldChange={handleFieldChange} disabled={disabled} onBlur={handleBlurNormalize} />
      ))}
      {Object.keys(errors).length > 0 && (
        <div role="alert" style={{ color: '#c00' }}>
          {Object.entries(errors).map(([k, msg]) => (<div key={k}>{k}: {String(msg)}</div>))}
        </div>
      )}
      <div className="form-actions">
        <button type="submit" disabled={disabled}>Submit</button>
      </div>
    </form>
  );
};

export default SchemaForm;
